import React from 'react';

import {Box, Typography} from '@mui/material';
import {styled, TypographyProps} from '@mui/system';

interface AddToCartV2Props {
  price: string;
  originalPrice: string;
  discountPercent: string | number;
  currency: string;
  slabPriceText?: string;
  kgText?: string;
}

const StyledBadgeWrapper = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'row',
  overflow: 'hidden',
  borderRadius: theme.spacing(0.75),
  backgroundColor: '#E82026',
  width: '100%'
})) as typeof Box;

const StyledLeftSection = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(0.75, 1.25),
  color: theme.palette.common.white,
  flexGrow: 1
})) as typeof Box;

const StyledCurrentPrice = styled(Typography)(({theme}) => ({
  fontSize: theme.spacing(2),
  fontWeight: 700,
  lineHeight: theme.spacing(3),
  color: theme.palette.common.white,
  marginLeft: theme.spacing(0.25)
})) as typeof Typography;

const StyledCurrency = styled(Typography)(({theme}) => ({
  fontSize: theme.spacing(1.5),
  fontWeight: 700,
  lineHeight: theme.spacing(3),
  color: theme.palette.common.white
})) as typeof Typography;

const StyledOriginalPrice = styled(Typography)(({theme}) => ({
  fontSize: theme.spacing(1.5),
  textDecoration: 'line-through',
  opacity: 0.8,
  color: theme.palette.common.white,
  lineHeight: theme.spacing(2),
  fontWeight: 400
})) as typeof Typography;

const StyledRightSection = styled(Box)(({theme}) => ({
  backgroundColor: '#AC0800',
  padding: '8px 4px 8px 4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: theme.spacing(1.75),
  color: theme.palette.common.white,
  position: 'relative',
  '&.slabPriceText': {
    display: 'block',
    alignItems: 'center'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '-10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 0,
    height: 0,
    borderTop: '36px solid transparent',
    borderBottom: '36px solid transparent',
    borderRight: '12px solid #AC0800'
  },
  maxWidth: '50%'
})) as typeof Box;

const StyledDiscountPercentageSection = styled(Typography)(({theme}) => ({
  display: 'flex',
  justifyContent: 'center',
  fontSize: theme.spacing(1.75),
  lineHeight: theme.spacing(2.5),
  fontWeight: 700
})) as typeof Box;

const StyledSlabPriceSection = styled(Typography)(({theme}) => ({
  fontSize: theme.spacing(1.25),
  lineHeight: theme.spacing(2),
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: '1'
})) as typeof Typography;

interface StyledDiscountWeightedFreshKgText extends TypographyProps {
  isLight?: boolean;
  isProductCardV2?: boolean;
}

const DiscountWeightedFreshKgText = styled(Typography)<StyledDiscountWeightedFreshKgText>(({theme}) => ({
  color: theme.palette.common.white,
  textAlign: 'center',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: theme.spacing(3),
  paddingLeft: theme.spacing(0.25),
  fontSize: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.spacing(1.25)
  },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  marginLeft: theme.spacing(0.25)
}));

const OriginalPriceDiscountWeightedFreshKgText = styled(Typography)<StyledDiscountWeightedFreshKgText>(({theme}) => ({
  fontSize: theme.spacing(1.5),
  opacity: 0.8,
  color: theme.palette.common.white,
  lineHeight: theme.spacing(2),
  fontWeight: 400,
  marginLeft: theme.spacing(0.25),
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.spacing(1.25)
  }
}));

const StylePriceSectionWrapper = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'baseline'
})) as typeof Box;

const AddToCartV2: React.FC<AddToCartV2Props> = ({
  price,
  originalPrice,
  discountPercent,
  currency,
  slabPriceText,
  kgText
}) => {
  const [whole, decimal] = price.split('.');
  return (
    <StyledBadgeWrapper>
      <StyledLeftSection>
        <StylePriceSectionWrapper>
          <StyledCurrency>{currency}</StyledCurrency>
          <StyledCurrentPrice>{whole}.</StyledCurrentPrice>
          <StyledCurrency>{decimal}</StyledCurrency>
          {kgText && <DiscountWeightedFreshKgText>{kgText}</DiscountWeightedFreshKgText>}
        </StylePriceSectionWrapper>
        <StylePriceSectionWrapper>
          <StyledOriginalPrice>
            {currency} {originalPrice}
          </StyledOriginalPrice>
          {kgText && <OriginalPriceDiscountWeightedFreshKgText>{kgText}</OriginalPriceDiscountWeightedFreshKgText>}
        </StylePriceSectionWrapper>
      </StyledLeftSection>
      {discountPercent || slabPriceText ? (
        <StyledRightSection className={slabPriceText ? 'slabPriceText' : ''}>
          {slabPriceText ? <StyledSlabPriceSection>{slabPriceText}</StyledSlabPriceSection> : <></>}
          {<StyledDiscountPercentageSection>{discountPercent}</StyledDiscountPercentageSection>}
        </StyledRightSection>
      ) : (
        <></>
      )}
    </StyledBadgeWrapper>
  );
};

export default AddToCartV2;
